// Session ACF: the office "Manage lifts" action. One admin-gated modal that adds or removes lifts on
// a running contract. It lists the lifts the contract covers plus any uncontracted lifts at the same
// site, lets the office tick which should be covered, set an effective date (today or later; never
// backdated) and an optional reason, and previews the old vs new price (repriced server-side from the
// full covered set, so the multi-lift discount bracket re-evaluates on the true count) before applying.
//
// SESSION AJJ: THE MODAL NOW HOSTS TWO DIFFERENT ACTIONS, and keeping them apart is the point.
//
//   CHANGE COVERAGE (the original, unchanged) -- a lift is genuinely joining or leaving, so the whole
//   covered set is repriced and Contract.annualValue moves.
//
//   LINK COVERAGE ONLY (new) -- a lift the contract was ALWAYS meant to cover, and is already priced
//   for, has no Asset.activeContractId. Fixing that pointer is a record correction, not a coverage
//   change: no engine runs, no money moves. Twelve live lifts sit in exactly this state (the V1
//   migration never wrote the pointer), and before this the only tool that could fix one was the
//   reprice path, which replaced Rosina Street's real GBP 797 with a GBP 640 list price -- a loss that
//   compounds for ever, because every renewal escalates from whatever is on record.
//
// The two are kept apart deliberately, not offered as a checkbox with a default:
//   - a mode control that STATES what each does, rather than a toggle beside the confirm button;
//   - two different selection meanings (the desired covered SET vs the lifts to LINK), held in two
//     separate pieces of state so one can never be submitted as the other;
//   - two different confirm buttons, with different labels, colours and handlers, only one rendered
//     at a time so the office cannot press the wrong one with a selection built for the other;
//   - two different server endpoints, one of which cannot reach the pricing engine at all.
//
// And in reprice mode the office is WARNED (advisory, never blocking) when the change would overwrite
// a price the engine cannot reproduce from the coverage the contract already holds -- the signal that
// "link coverage only" is probably the action they wanted.
import { useCallback, useEffect, useMemo, useState } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import {
  api,
  ApiError,
  type ContractCoverage,
  type ContractCoverageLinkPreview,
  type ContractCoveragePreview,
  type CoverageLift,
} from "../../lib/api";
import { AGE_BAND_LABEL, DURATION_BAND_NAME, DURATION_BAND_RANGE, formatPriceMult } from "../../lib/quotePricing";
import type { AgeBand, QuoteDuration } from "../../lib/quotePricing";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

const btn =
  "rounded-ele px-4 py-2 font-menu text-xs tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

function liftLabel(l: CoverageLift): string {
  return [l.eleNumber ?? "Lift", l.liftType].filter(Boolean).join(" · ");
}

type Mode = "reprice" | "link";

export function ManageLiftsModal({
  contractId,
  coverage,
  onClose,
  onApplied,
}: {
  contractId: string;
  coverage: ContractCoverage;
  onClose: () => void;
  onApplied: (next: ContractCoverage) => void;
}) {
  // Every lift the office may act on: currently covered + uncontracted-at-site. Covered start ticked.
  const allLifts = useMemo(() => [...coverage.covered, ...coverage.available], [coverage]);
  const coveredIds = useMemo(() => new Set(coverage.covered.map((l) => l.id)), [coverage]);

  // The original action is the DEFAULT. A session that quietly changed which action an existing
  // button performs would be a worse failure than the one this feature fixes.
  const [mode, setMode] = useState<Mode>("reprice");

  // TWO SELECTIONS WITH TWO DIFFERENT MEANINGS, held apart on purpose. `selected` is the full desired
  // covered set (reprice mode); `linkSelected` is the add-only list of lifts to link. Sharing one set
  // would make the link payload a function of which mode happened to be showing.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(coverage.covered.map((l) => l.id)));
  const [linkSelected, setLinkSelected] = useState<Set<string>>(() => new Set());
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [effectiveDate, setEffectiveDate] = useState(todayIso);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<ContractCoveragePreview | null>(null);
  const [linkPreview, setLinkPreview] = useState<ContractCoverageLinkPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const linkSelectedIds = useMemo(() => [...linkSelected], [linkSelected]);
  const changed = useMemo(() => {
    if (selected.size !== coveredIds.size) return true;
    for (const id of selected) if (!coveredIds.has(id)) return true;
    return false;
  }, [selected, coveredIds]);

  // A future effective date defers the change: it is recorded now but nothing (price, coverage) moves
  // until that date, when the daily job applies it. A same-day date applies at once. Link mode has no
  // effective date at all: a pointer correction states what was already true, so there is nothing to
  // schedule.
  const deferred = mode === "reprice" && effectiveDate > todayIso;
  // A contract holds at most one outstanding scheduled change; while one exists the office must apply
  // (it happens on its date) or cancel it before scheduling another. The server enforces this for BOTH
  // actions (409): linking a lift underneath a pending amendment would silently widen what the cron
  // reprices on its effective date.
  const pending = coverage.pendingAmendment;

  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setError(null);
    setPreview(null);
    setLinkPreview(null);
    // Each mode starts from its own clean slate rather than inheriting the other's ticks. The REASON
    // is cleared with them: a note typed to explain a reprice ("incumbent contract ended") would be
    // false on the link-only amendment it would otherwise be filed against.
    setSelected(new Set(coverage.covered.map((l) => l.id)));
    setLinkSelected(new Set());
    setReason("");
  }, [coverage]);

  const toggle = useCallback((l: CoverageLift) => {
    if (!l.priceable && !coveredIds.has(l.id)) return; // cannot ADD an unpriceable lift to a REPRICE
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(l.id)) next.delete(l.id);
      else next.add(l.id);
      return next;
    });
  }, [coveredIds]);

  // Link mode ticks a lift for LINKING. An already-covered lift has nothing to link, and -- unlike the
  // reprice path -- an UNPRICEABLE lift is fully tickable here, because a link runs no engine and
  // refusing would keep the platform's record of what ELE covers wrong to protect a calculation
  // nobody asked for.
  const toggleLink = useCallback((l: CoverageLift) => {
    if (coveredIds.has(l.id)) return;
    setLinkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(l.id)) next.delete(l.id);
      else next.add(l.id);
      return next;
    });
  }, [coveredIds]);

  // Live price preview whenever the selection changes and differs from the current coverage.
  useEffect(() => {
    if (mode !== "reprice") return;
    if (!changed || selected.size === 0) {
      setPreview(null);
      return;
    }
    let live = true;
    setPreviewBusy(true);
    setError(null);
    api.contracts
      .coveragePreview(contractId, selectedIds, effectiveDate)
      .then((p) => {
        if (live) setPreview(p);
      })
      .catch((err) => {
        if (!live) return;
        setPreview(null);
        setError(err instanceof ApiError ? err.message || err.code : "Could not price that selection.");
      })
      .finally(() => {
        if (live) setPreviewBusy(false);
      });
    return () => {
      live = false;
    };
  }, [contractId, selectedIds, effectiveDate, changed, selected.size, mode]);

  // Link preview. Nothing is priced: it confirms which pointers would move and restates the annual
  // value that is NOT changing.
  useEffect(() => {
    if (mode !== "link") return;
    if (linkSelected.size === 0) {
      setLinkPreview(null);
      return;
    }
    let live = true;
    setPreviewBusy(true);
    setError(null);
    api.contracts
      .coverageLinkPreview(contractId, linkSelectedIds)
      .then((p) => {
        if (live) setLinkPreview(p);
      })
      .catch((err) => {
        if (!live) return;
        setLinkPreview(null);
        setError(err instanceof ApiError ? err.message || err.code : "Could not check that selection.");
      })
      .finally(() => {
        if (live) setPreviewBusy(false);
      });
    return () => {
      live = false;
    };
  }, [contractId, linkSelectedIds, linkSelected.size, mode]);

  const apply = useCallback(async () => {
    setApplyBusy(true);
    setError(null);
    try {
      const next = await api.contracts.coverageApply(contractId, { assetIds: selectedIds, effectiveDate, reason: reason.trim() || undefined });
      onApplied(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not apply the change. Please try again.");
      setApplyBusy(false);
    }
  }, [contractId, selectedIds, effectiveDate, reason, onApplied]);

  const applyLink = useCallback(async () => {
    setApplyBusy(true);
    setError(null);
    try {
      const next = await api.contracts.coverageLinkApply(contractId, { assetIds: linkSelectedIds, reason: reason.trim() || undefined });
      onApplied(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not link that coverage. Please try again.");
      setApplyBusy(false);
    }
  }, [contractId, linkSelectedIds, reason, onApplied]);

  // Require a SUCCESSFUL, priceable, non-empty preview before applying (a failed preview leaves
  // preview null; applying then would be blind even though the server reprices authoritatively). A
  // pending change already scheduled blocks a new one (must be applied/cancelled first).
  const canApply =
    !pending && changed && selected.size > 0 && !applyBusy && !previewBusy && preview !== null && !preview.noChange && preview.unpriceable.length === 0;
  // The link confirm has no unpriceable gate, because a link never prices.
  const canLink = !pending && linkSelected.size > 0 && !applyBusy && !previewBusy && linkPreview !== null && linkPreview.link.length > 0;

  // Whether the breakdown is worth reading unfolded: more than one lift, or any single lift that
  // is not simply riding the anchor case (every multiplier 1x, no manual-doors surcharge). Decides
  // the details element's default open state only; a same-selection re-render never overrides a
  // manual toggle (see the key on the element below).
  const breakdownNonTrivial =
    !!preview &&
    (preview.breakdown.length > 1 ||
      preview.breakdown.some((row) => row.typeMult !== 1 || row.durationMult !== 1 || row.ageMult !== 1 || row.manualDoors));

  // The advisory is HIDDEN while the next preview is in flight. Its whole content is two money
  // figures for a specific selection, so leaving the old one up while the office reticks a box has it
  // asserting a from/to pair that belongs to a selection nobody is looking at any more.
  const risk = mode === "reprice" && !previewBusy ? preview?.legacyPriceRisk ?? null : null;
  // "Link coverage only" is offered as the alternative ONLY when it genuinely is one: it can add a
  // pointer and can never drop one, so on a change that also REMOVES a lift, switching would silently
  // discard the removal the office had just made.
  const removingSomething = useMemo(() => [...coveredIds].some((id) => !selected.has(id)), [coveredIds, selected]);
  const linkIsTheAlternative = risk != null && risk.reason !== "covers_more_than_lifts" && !removingSomething;

  const modeBtn = (m: Mode) =>
    `flex-1 rounded-ele border px-3 py-2 text-left transition-colors ${
      mode === m ? "border-ele-purple-accent bg-ele-purple/20 text-ele-strong" : "border-ele-hairline text-ele-body hover:border-ele-purple-accent"
    }`;

  return (
    <ModalShell ariaLabel="Manage lifts" onClose={() => !applyBusy && onClose()} width="max-w-lg" panelClassName="p-6 text-left">
        <h2 className="font-heading text-xl uppercase tracking-wide text-ele-strong">Manage lifts</h2>

        {/* The two actions, named and described. Not a toggle beside the confirm button: which one is
            active decides what the ticks below mean and which confirm button appears at the bottom. */}
        <div className="mt-3 flex gap-2" role="group" aria-label="What kind of change is this?">
          <button type="button" onClick={() => switchMode("reprice")} aria-pressed={mode === "reprice"} disabled={applyBusy} className={modeBtn("reprice")}>
            <span className="block font-menu text-xs uppercase tracking-wide">Change coverage</span>
            <span className="mt-0.5 block text-xs text-ele-body">A lift is joining or leaving. The contract is repriced.</span>
          </button>
          <button type="button" onClick={() => switchMode("link")} aria-pressed={mode === "link"} disabled={applyBusy} className={modeBtn("link")}>
            <span className="block font-menu text-xs uppercase tracking-wide">Link coverage only</span>
            <span className="mt-0.5 block text-xs text-ele-body">A lift was always covered. The price does not change.</span>
          </button>
        </div>

        <p className="mt-3 text-sm text-ele-body">
          {mode === "reprice"
            ? "Tick the lifts this contract should cover. The price is recalculated across every covered lift, so the multi-lift discount always matches the true number. Changes take effect on the date you set and are never backdated."
            : "Tick the lifts that this contract already covers but that are not recorded against it. This only corrects the record: the annual value stays exactly as it is, and nothing is recalculated."}
        </p>

        {pending && (
          <p role="alert" className="mt-3 rounded-ele border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
            A lift change is already scheduled for {formatDate(pending.effectiveDate)}. Apply it
            (it happens automatically on that date) or cancel it from the amendment history before making another change.
          </p>
        )}

        <div className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1">
          {allLifts.length === 0 ? (
            <p className="text-sm text-ele-body">No lifts are on file for this site yet.</p>
          ) : (
            allLifts.map((l) => {
              const isCovered = coveredIds.has(l.id);
              const isChecked = mode === "reprice" ? selected.has(l.id) : isCovered || linkSelected.has(l.id);
              // Reprice mode cannot ADD an unpriceable lift. Link mode cannot touch an already-covered
              // one (there is no pointer left to fix).
              const locked = mode === "reprice" ? !l.priceable && !isCovered : isCovered;
              return (
                <label
                  key={l.id}
                  className={`flex items-center gap-3 rounded-ele border px-3 py-2 ${
                    locked ? "border-ele-hairline-soft opacity-50" : "border-ele-hairline hover:border-ele-purple-accent"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={locked || applyBusy}
                    onChange={() => (mode === "reprice" ? toggle(l) : toggleLink(l))}
                    className="h-4 w-4 accent-ele-green"
                  />
                  <span className="min-w-0 flex-1 text-sm text-ele-strong">
                    {liftLabel(l)}
                    {isCovered ? <span className="ml-2 text-xs text-ele-green">covered</span> : <span className="ml-2 text-xs text-ele-body">available</span>}
                    {!l.priceable && !isCovered && (
                      <span className="ml-2 text-xs text-amber-400">
                        {mode === "reprice" ? "cannot price automatically" : "cannot price automatically (linking does not price it)"}
                      </span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {mode === "reprice" && (
            <label className="flex flex-col gap-1">
              <span className="font-menu text-xs uppercase tracking-wide text-ele-body">Effective date</span>
              <input
                type="date"
                min={todayIso}
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="rounded-ele border border-ele-hairline bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
              />
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="font-menu text-xs uppercase tracking-wide text-ele-body">Reason (optional)</span>
            <input
              type="text"
              value={reason}
              maxLength={2000}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === "reprice" ? "e.g. incumbent contract ended" : "e.g. never recorded at migration"}
              className="rounded-ele border border-ele-hairline bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
            />
          </label>
        </div>

        {deferred && changed && (
          <p className="mt-3 text-sm text-ele-blue">
            This change is scheduled for {formatDate(`${effectiveDate}T00:00:00Z`)}. It is recorded
            now, but the price, coverage and billing do not move until that date, when it is applied automatically.
          </p>
        )}

        {/* Session AJJ (Item 3). ADVISORY, and it never disables the confirm button below: the office
            makes the call. It appears only when repricing would genuinely overwrite a figure the
            calculator cannot reproduce, which is the case where a missing link is the likelier
            explanation than a real coverage change. */}
        {risk && (
          // Amber advisory. The ink and the border are the DOCUMENT amber pair (#b8860b flat, #8a6508
          // ink on the #fbf3dd tint) rather than Tailwind's amber-200/500 wash: this modal portals to
          // document.body but re-applies `office-ui`, so it renders on the LIGHT office ground, where
          // amber-200 text measured 1.15:1 and the amber-500/60 border 1.51:1. Both failed AA.
          <div role="alert" className="mt-3 rounded-ele border border-ele-amber-line bg-ele-amber-wash p-3 text-sm text-ele-amber-wash-ink">
            <p className="font-semibold">
              This will change the contract's annual value from {gbp(risk.currentAnnualValue)} to {gbp(risk.newAnnualValue)}.
            </p>
            <p className="mt-1">
              {risk.reason === "covers_more_than_lifts"
                ? `This contract also covers ${risk.coveredEquipmentCount} item${risk.coveredEquipmentCount === 1 ? "" : "s"} that ${risk.coveredEquipmentCount === 1 ? "is" : "are"} not a lift. The calculator prices lifts only, so applying this replaces the whole annual value with a lifts-only figure and the money for those items is dropped.`
                : risk.coveredLiftCount === 0
                  ? `This contract charges ${gbp(risk.currentAnnualValue)} a year but has nothing recorded against it at all, so the lift you are adding was almost certainly already priced into that figure.`
                  : `The calculator makes ${gbp(risk.listPriceOfCurrentCoverage)} a year of the lifts already recorded against this contract, not the ${gbp(risk.currentAnnualValue)} it charges, so the stored price is not a calculator price.`}{" "}
              Every future renewal is escalated from whatever is recorded at that point.
            </p>
            {linkIsTheAlternative && (
              <>
                <p className="mt-1">
                  If this lift was already included in the {gbp(risk.currentAnnualValue)}, use Link coverage only instead: it
                  records the lift against the contract without changing the price.
                </p>
                <button
                  type="button"
                  onClick={() => switchMode("link")}
                  disabled={applyBusy}
                  className="mt-2 rounded-ele border border-ele-amber-line px-3 py-1.5 font-menu text-xs tracking-wide text-ele-amber-wash-ink transition-colors hover:bg-ele-amber-wash-hover"
                >
                  Switch to Link coverage only
                </button>
              </>
            )}
            {risk.reason !== "covers_more_than_lifts" && removingSomething && (
              // Deliberately NOT offered as a switch: linking cannot remove a lift, so the button
              // would silently drop the removal the office just made.
              <p className="mt-1">
                Linking is not an alternative here, because this change also takes a lift off the contract and a link can only
                add one. Check the price with the office before applying.
              </p>
            )}
          </div>
        )}

        {/* Old vs new price (reprice), or the one unchanged figure (link). */}
        <div className="mt-4 rounded-ele border border-ele-hairline bg-ele-surface p-3 text-sm">
          {mode === "link" ? (
            linkSelected.size === 0 ? (
              <p className="text-ele-body">Tick a lift above to link it to this contract.</p>
            ) : previewBusy ? (
              <p className="text-ele-body" aria-live="polite">Checking...</p>
            ) : linkPreview ? (
              <div>
                {/* ONE figure, not struck through: there is no new price, because none is calculated. */}
                <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-ele-body">Annual value</span>
                  <span className="text-lg font-semibold text-ele-strong">{gbp(linkPreview.annualValue)} ex VAT</span>
                  <span className="text-xs text-ele-green">not changing</span>
                </p>
                <p className="mt-1 text-xs text-ele-body">
                  {linkPreview.link.length} lift{linkPreview.link.length === 1 ? "" : "s"} will be recorded against this contract:{" "}
                  {linkPreview.link.map((l) => l.eleNumber ?? "Lift").join(", ")}. Nothing is recalculated and the annual value,
                  per-visit value and billing frequency are left exactly as they are.
                </p>
                {linkPreview.alreadyLinked.length > 0 && (
                  // Without this a ticked lift simply vanishes from the summary with no explanation.
                  <p className="mt-1 text-xs text-ele-body">
                    {linkPreview.alreadyLinked.map((l) => l.eleNumber ?? "A lift").join(", ")} is already recorded against this
                    contract, so nothing is written for it.
                  </p>
                )}
                {linkPreview.unpriceable.length > 0 && (
                  <p className="mt-1 text-xs text-ele-amber-wash-ink">
                    {linkPreview.unpriceable.map((l) => l.eleNumber ?? "A lift").join(", ")} cannot be priced automatically. The
                    link itself is unaffected, but until a recognised lift type is set on the lift record this contract cannot
                    be repriced, and its customer renewal page will not price either.
                  </p>
                )}
              </div>
            ) : null
          ) : !changed ? (
            <p className="text-ele-body">Make a change above to see the new price.</p>
          ) : previewBusy ? (
            <p className="text-ele-body" aria-live="polite">Pricing...</p>
          ) : preview ? (
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-ele-body">Annual value</span>
              <span className="text-ele-body line-through">{gbp(preview.previousAnnualValue)}</span>
              <span className="text-lg font-semibold text-ele-green">{gbp(preview.newAnnualValue)} ex VAT</span>
              <span className="text-xs text-ele-body">
                {preview.addedCount > 0 && `+${preview.addedCount} added`} {preview.removedCount > 0 && `-${preview.removedCount} removed`}
              </span>
              {preview.unpriceable.length > 0 && (
                <p className="w-full text-xs text-amber-400">
                  Some selected lifts cannot be priced automatically. Set a recognised lift type on each first.
                </p>
              )}
            </div>
          ) : null}
        </div>

        {mode === "reprice" && preview && preview.breakdown.length > 0 && (
          <details
            key={selectedIds.join(",")}
            open={breakdownNonTrivial}
            className="mt-3 rounded-ele border border-ele-hairline bg-ele-surface p-3 text-sm"
          >
            <summary className="cursor-pointer font-menu text-xs tracking-wide text-ele-blue">
              Pricing breakdown ({preview.breakdown.length} lift{preview.breakdown.length === 1 ? "" : "s"})
            </summary>
            <div className="mt-2 space-y-3">
              {preview.breakdown.map((row) => (
                <div key={row.assetId} className="border-t border-ele-hairline pt-2 first:border-t-0 first:pt-0">
                  <p className="text-sm font-semibold text-ele-strong">{[row.eleNumber ?? "Lift", row.liftTypeLabel].filter(Boolean).join(" · ")}</p>
                  <p className="text-xs text-ele-body">{`Type multiplier: ${formatPriceMult(row.typeMult)}`}</p>
                  <p className="text-xs text-ele-body">{`Duration band: ${DURATION_BAND_NAME[row.duration as QuoteDuration]} (${DURATION_BAND_RANGE[row.duration as QuoteDuration]}, x${formatPriceMult(row.durationMult)})`}</p>
                  <p className="text-xs text-ele-body">
                    {row.ageBand
                      ? `Age band: ${AGE_BAND_LABEL[row.ageBand as AgeBand]} (x${formatPriceMult(row.ageMult)})`
                      : `Age band: not recorded (x${formatPriceMult(row.ageMult)} default)`}
                  </p>
                  <p className="text-xs text-ele-body">
                    {row.manualDoors ? `Manual doors: ${gbp(row.manualDoorsAnnual)}/yr surcharge` : "Manual doors: not applicable"}
                  </p>
                </div>
              ))}
            </div>
          </details>
        )}

        {error && <p role="alert" className="mt-3 text-sm text-ele-danger">{error}</p>}

        <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={() => { if (!applyBusy) onClose(); }} disabled={applyBusy} className={`${btn} border border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          {/* One confirm button at a time, so a selection built in one mode can never be submitted by
              the other's handler. Different label, different colour, different endpoint. */}
          {mode === "reprice" ? (
            <Button onClick={apply} disabled={!canApply} className={MODAL_ACTION_BTN}>
              {applyBusy ? (deferred ? "Scheduling..." : "Applying...") : deferred ? "Schedule change" : "Apply change"}
            </Button>
          ) : (
            <button
              onClick={applyLink}
              disabled={!canLink}
              className={`${btn} border border-ele-blue text-ele-blue transition-colors hover:bg-ele-blue hover:text-ele-bg ${MODAL_ACTION_BTN}`}
            >
              {/* Deliberately NOT "Link coverage only", which is the MODE's name: two controls sharing
                  one accessible name is ambiguous to a screen reader and to a test, and pressing the
                  wrong one wipes the office's selection instead of submitting it. */}
              {applyBusy ? "Linking..." : "Link these lifts"}
            </button>
          )}
        </div>
    </ModalShell>
  );
}
